import { Fragment } from 'react';

import { VENUES_TAXONOMY_KEY } from '@zola-helpers/client/dist/es/marketplace/vendorTaxonomyKeys';
import { CheckboxFieldAdapter } from '@zola/zola-ui/src/components/Form/CheckboxField';
import {
  GREATER_THAN_ZERO,
  GREATER_THAN_ZERO_OR_BLANK,
} from '@zola/zola-ui/src/components/Form/util/validations';

import { Field } from 'react-final-form';

import { maxIntegerValue } from '~/components/common/form/commonValidations';
import { NumberInputWithCommas } from '~/components/common/zolaUI/NumberInputWithCommas/NumberInputWithCommas';
import { CouplesStorefrontDetails } from '~/types/storefrontDetails';
import { formatStringToNumber } from '~/util/formatters';

import { useInquiryValues } from '../../context/InquiryContext';
import { LoadedInquiryValues } from '../../context/types';
import { InquiryFormValues } from '../../types';

import styles from './weddingBudgetField.module.less';

export const FIELD_NAMES: (keyof InquiryFormValues)[] = [
  'flexibleOnWeddingPrice',
  'weddingEndPrice',
];

export const isAnswered = ({
  initialValues,
  storefront,
}: {
  initialValues: InquiryFormValues;
  storefront: CouplesStorefrontDetails;
}): boolean => {
  if (storefront.taxonomyKey === VENUES_TAXONOMY_KEY) {
    return Boolean(initialValues.weddingEndPrice);
  }

  // Field is optional for non-venue inquiries
  return true;
};

/**
 * value may come through from RFF as a string with a comma in it.
 * maxInteger expects a number so we convert it.
 */
export const maxWeddingPriceValidation = (value: string | undefined): string | undefined => {
  const formattedNum = formatStringToNumber(value);
  return maxIntegerValue(1000000)(formattedNum);
};

/**
 * value may come through from RFF as a string with a comma in it.
 * GREATER_THAN_ZERO expects a number so we convert it.
 */
export const greaterThanZeroStringToNum = (value: string | undefined): string | undefined => {
  const num = formatStringToNumber(value);
  return GREATER_THAN_ZERO(num);
};

export const WeddingBudgetField = ({ showLabel = true }: { showLabel?: boolean }) => {
  const { storefront } = useInquiryValues() as LoadedInquiryValues;
  const { taxonomyKey } = storefront;

  if (taxonomyKey === VENUES_TAXONOMY_KEY) {
    return <Fragment />;
  }

  return (
    <fieldset className={styles.weddingBudgetField}>
      {showLabel && <legend>Overall wedding budget</legend>}
      <p>
        This helps us recommend other vendors fit for you, plus relevant tips, advice, and more.
      </p>
      <div className={styles.budgetFieldsWrapper}>
        <NumberInputWithCommas
          name="weddingEndPrice"
          data-testid="weddingEndPrice"
          containerClassname={styles.budgetFieldInput}
          validators={[GREATER_THAN_ZERO_OR_BLANK, maxWeddingPriceValidation]}
          displayedField="weddingEndPriceAsCurrency"
          type="currency"
        />
        <Field
          id="flexibleOnWeddingPrice"
          name="flexibleOnWeddingPrice"
          tabIndex={0}
          component={CheckboxFieldAdapter}
          label="This is flexible"
          inline
          className={styles.flexibleOnPrice}
          type="checkbox"
          validateFields={[]}
        />
      </div>
    </fieldset>
  );
};

const WeddingBudgetFieldModule = {
  isAnswered,
  FIELD_NAMES,
};

export default WeddingBudgetFieldModule;
